import { handleContentfulError, handleFetchError } from 'libs/fetch/errorHandler';
import { minifyGraphQLQuery } from 'libs/fetch/minifyQuery';
import { isLocalEnvironment } from 'utils/environment';

const {
  NEXT_PUBLIC_CONTENTFUL_ENV,
  NEXT_PUBLIC_CONTENTFUL_SPACE_ID,
  NEXT_PUBLIC_CONTENTFUL_ACCESS_TOKEN,
  NEXT_PUBLIC_CONTENTFUL_PREVIEW_ACCESS_TOKEN,
} = process.env;

/**
 * Fetches data from Contentful using the provided GraphQL query.
 * Optionally fetches preview content and associates cache tags for revalidation.
 *
 * @param query - The GraphQL query string.
 * @param draftPreviewMode - If true, fetches content including ones in draft; defaults to false.
 * @param variables - Optional GraphQL variables object.
 * @returns A Promise that resolves to the JSON response from Contentful.
 */
export async function fetchGraphQL<T = { data: Record<string, unknown> }>(
  query: string,
  draftPreviewMode = false,
  variables: Record<string, unknown> = {},
): Promise<T> {
  const queryHash = await generateSHA256Hash(minifyGraphQLQuery(query));
  const contentfulEnv = getContentfulEnv();
  const contentfulUrl = `https://graphql.contentful.com/content/v1/spaces/${NEXT_PUBLIC_CONTENTFUL_SPACE_ID}/environments/${contentfulEnv}`;

  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${
      draftPreviewMode ? NEXT_PUBLIC_CONTENTFUL_PREVIEW_ACCESS_TOKEN : NEXT_PUBLIC_CONTENTFUL_ACCESS_TOKEN
    }`,
  };

  const getQueryResponse = async (response: Response) => {
    if (isLocalEnvironment()) {
      logElevatedQueryCosts(response, query);
    }

    const handledResponse = await handleFetchError(response);
    const results = await handledResponse.json();

    return handleContentfulError(results) as T;
  };

  const extensions = {
    persistedQuery: {
      version: 1,
      sha256Hash: queryHash,
    },
  };

  const persistedQueryRequest = {
    method: 'POST',
    headers: {
      ...headers,
      'Persisted-Query': 'true',
    },
    body: JSON.stringify({
      extensions,
      variables,
    }),
  };

  // First, try to execute the query using only the hash
  const persistedQueryResponse = await fetch(contentfulUrl, persistedQueryRequest);

  const requestBody = JSON.stringify({
    query: minifyGraphQLQuery(query),
    extensions,
    variables,
  });

  // Log query size in local environment (only when sending full query)
  if (isLocalEnvironment()) {
    logElevatedQuerySize(requestBody, 'full query');
  }

  if (persistedQueryResponse.ok) {
    return getQueryResponse(persistedQueryResponse);
  }

  const fullQueryRequest = {
    method: 'POST',
    headers,
    body: requestBody,
  };

  // If the query is not persisted yet (404), send the full query
  const fullQueryResponse = await fetch(contentfulUrl, fullQueryRequest);

  return getQueryResponse(fullQueryResponse);
}

// Helper function to generate SHA256 hash
async function generateSHA256Hash(query: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(query);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(byte => byte.toString(16).padStart(2, '0')).join('');
}

// Contentful configuration using Next.js env injection
export const contentfulConfig = {
  // These are injected by Next.js from server env vars for both client and server
  environment: getContentfulEnv(),
  spaceId: NEXT_PUBLIC_CONTENTFUL_SPACE_ID || '',
  accessToken: NEXT_PUBLIC_CONTENTFUL_ACCESS_TOKEN || '',
  previewAccessToken: NEXT_PUBLIC_CONTENTFUL_PREVIEW_ACCESS_TOKEN || '',
};

// Get Contentful env
function getContentfulEnv() {
  return NEXT_PUBLIC_CONTENTFUL_ENV || 'master';
}

/**
 * current runs in local dev only.
 * Extracts query cost from the Response header,
 * logs percentage vs. 100101, warns if >= 65,000.
 */
async function logElevatedQueryCosts(response: Response, query: string) {
  const MAX_COST = 100101;
  const rawCost = response.headers.get('X-Contentful-Graphql-Query-Cost');
  const cost = rawCost ? parseInt(rawCost, 10) : 0;
  const percent = ((cost / MAX_COST) * 100).toFixed(2);
  const querySnippet = query.replace(/\s+/g, ' ').slice(0, 70);

  const logLine = `[Contentful Query Complexity]: ${cost} (${percent}%) "${querySnippet}"`;

  if (cost >= 65000) {
    console.warn(`⚠️  WARNING! ${logLine}`);
  } else if (cost >= 30000) {
    // eslint-disable-next-line no-console
    console.info(`🔶  INFO: ${logLine}`);
  }
}

/**
 * Runs in local dev only.
 * Calculates the actual byte size of the outgoing query payload,
 * logs percentage vs. 16384 bytes (16KB), warns if >=65%.
 */
function logElevatedQuerySize(query: string, queryType: string) {
  const MAX_SIZE = 16384; // 16KB
  const byteSize = new TextEncoder().encode(query).length;
  const percent = ((byteSize / MAX_SIZE) * 100).toFixed(2);
  const querySnippet = query.replace(/\s+/g, ' ').slice(0, 70);

  const logLine = `[Contentful Query Size]: ${byteSize} bytes (${percent}%) "${querySnippet}"`;

  if (byteSize >= MAX_SIZE) {
    console.warn(`❌ ERROR! ${logLine} (${queryType})`);
  } else if (byteSize >= MAX_SIZE * 0.65) {
    console.warn(`⚠️  WARNING! ${logLine} (${queryType})`);
  } else if (byteSize >= MAX_SIZE * 0.5) {
    // eslint-disable-next-line no-console
    console.info(`🔶  INFO: ${logLine} (${queryType})`);
  }
}
